<template>
  <div class="landing-wrapper">
    <v-container class="py-8 py-md-12" fluid>
      <v-row justify="center">
        <v-col cols="12" lg="10">
          <v-alert v-if="error" type="error" class="mb-4" closable @click:close="error = ''">
            {{ error }}
          </v-alert>

          <div v-if="loading" class="d-flex justify-center py-8">
            <v-progress-circular indeterminate color="primary" size="48" />
          </div>

          <template v-else>
            <!-- Barra de géneros con el contador de pendientes -->
            <GenreTabs
              :genres="genres"
              :counts="countByGenre"
              :active-slug="activeSlug"
              @select="selectGenre"
            />

            <h2 class="sc-title">
              CONTROL VENTAS<template v-if="activeGenre"> / {{ activeGenre.name.toUpperCase() }}</template>
            </h2>

            <div v-if="activePlays.length" class="sc-cards">
              <button
                v-for="p in activePlays"
                :key="p.id"
                type="button"
                class="sc-card"
                :class="{ 'sc-card-alert': p.pending > 0 }"
                @click="goToPlay(p)"
              >
                <span v-if="p.pending" class="sc-card-badge">{{ p.pending }}</span>
                <span class="sc-card-title">{{ p.title }}</span>
                <span class="sc-card-company">{{ companyName(p) }}</span>
                <span class="sc-card-dates">{{ availability(p) }}</span>
              </button>
            </div>

            <p v-else class="sc-empty">
              No hay obras en este género.
            </p>
          </template>
        </v-col>
      </v-row>
    </v-container>

    <teatro-footer />
  </div>
</template>

<script>
import { fetchPlaysAdmin, fetchGenres, fetchMyReservations } from "@/services/teatroApi";
import { needsActionFrom } from "./reservationStatus";
import GenreTabs from "./GenreTabs.vue";

export default {
  name: "SalesControl",
  components: { GenreTabs },
  data() {
    return {
      genres: [],
      plays: [],
      reservations: [],
      activeSlug: "",
      loading: true,
      error: "",
    };
  },
  computed: {
    viewer() {
      const isAdmin = this.$store?.state?.is_admin === true;
      const role = this.$store?.state?.role;
      return {
        isAdmin,
        isManager: isAdmin || role === "theater_member",
        isClient: role === "client",
      };
    },
    // playId -> nº de reservas esperando una acción de quien mira.
    pendingByPlay() {
      const map = {};
      for (const r of this.reservations) {
        const pid = r.play?.id;
        if (!pid) continue;
        const date = r.displayDate || r.confirmedDate || r.requestedDate || null;
        if (!needsActionFrom(r.status, this.viewer, date, r)) continue;
        map[pid] = (map[pid] || 0) + 1;
      }
      return map;
    },
    // Obras con su contador, ya resueltas contra pendingByPlay.
    playsWithPending() {
      return this.plays.map((p) => ({
        ...p,
        pending: this.pendingByPlay[p.id] || 0,
      }));
    },
    // slug del género -> suma de los pendientes de sus obras (badge de la pestaña).
    countByGenre() {
      const map = {};
      for (const p of this.playsWithPending) {
        const slug = p.genre?.slug;
        if (!slug) continue;
        map[slug] = (map[slug] || 0) + p.pending;
      }
      return map;
    },
    activeGenre() {
      return this.genres.find((g) => g.slug === this.activeSlug) || null;
    },
    activePlays() {
      if (!this.activeSlug) return [];
      return this.playsWithPending.filter((p) => p.genre?.slug === this.activeSlug);
    },
  },
  async created() {
    await this.loadAuth();
    await this.load();
  },
  methods: {
    async loadAuth() {
      try {
        await this.$store.dispatch("fetchIsAuthenticated");
      } catch (e) {
        console.error(e);
      }
    },
    async load() {
      this.loading = true;
      this.error = "";
      try {
        const [genres, plays, reservations] = await Promise.all([
          fetchGenres(),
          fetchPlaysAdmin(),
          fetchMyReservations(),
        ]);
        this.genres = genres || [];
        this.plays = plays || [];
        this.reservations = reservations || [];
        this.activeSlug = this.initialSlug();
      } catch (e) {
        this.error = "No se pudo cargar el control de ventas.";
        console.error(e);
      } finally {
        this.loading = false;
      }
    },
    // Género inicial: el de la URL (?genero=) si es válido; si no, el primero
    // con obras; si ninguno tiene, el primero de la lista.
    initialSlug() {
      const wanted = new URLSearchParams(window.location.search).get("genero");
      if (wanted && this.genres.some((g) => g.slug === wanted)) return wanted;
      const withPlays = this.genres.find((g) =>
        this.plays.some((p) => p.genre?.slug === g.slug)
      );
      return withPlays?.slug || this.genres[0]?.slug || "";
    },
    selectGenre(slug) {
      this.activeSlug = slug;
      // Mantener la pestaña en la URL para que "volver" desde el detalle la respete.
      const url = new URL(window.location.href);
      url.searchParams.set("genero", slug);
      window.history.replaceState({}, "", url);
    },
    companyName(p) {
      return (p.company?.name || "").toUpperCase() || "—";
    },
    formatDate(iso) {
      if (!iso) return "";
      const [y, m, d] = String(iso).slice(0, 10).split("-");
      return `${d}/${m}/${y.slice(2)}`;
    },
    availability(p) {
      const start = this.formatDate(p.start_date);
      const end = this.formatDate(p.end_date);
      if (start && end) return `${start} - ${end}`;
      return start || end || "—";
    },
    goToPlay(p) {
      const q = this.activeSlug ? `?genero=${encodeURIComponent(this.activeSlug)}` : "";
      window.location.href = `/ventas/obra/${p.id}/${q}`;
    },
  },
};
</script>

<style scoped>
.sc-title {
  text-align: center;
  margin: 28px 0 26px;
  color: var(--color-theater-text);
  font-family: var(--font-family-heading);
  font-size: 26px;
  font-weight: 400;
  letter-spacing: 1.4px;
}

.sc-cards {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(168px, 1fr));
  gap: 18px;
}

.sc-card {
  position: relative;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 4px;
  min-height: 96px;
  padding: 18px 14px;
  background: rgba(8, 5, 5, 0.55);
  border: 1px solid rgba(255, 255, 255, 0.14);
  border-radius: 10px;
  cursor: pointer;
  text-align: center;
  transition: transform 0.15s ease, border-color 0.15s ease, background 0.15s ease;
}

.sc-card:hover {
  transform: translateY(-2px);
  border-color: rgba(255, 255, 255, 0.4);
  background: rgba(20, 12, 12, 0.75);
}

.sc-card-alert {
  border-color: #c8102e;
}

.sc-card-badge {
  position: absolute;
  top: -9px;
  right: -9px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 22px;
  height: 22px;
  padding: 0 6px;
  background: #c8102e;
  color: #fff;
  border-radius: 999px;
  font-family: var(--font-family-heading);
  font-size: 11px;
  font-weight: 800;
  line-height: 1;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.5);
}

.sc-card-title {
  color: var(--color-theater-text);
  font-family: var(--font-family-heading);
  font-size: 14px;
  font-weight: 800;
  letter-spacing: 0.6px;
  text-transform: uppercase;
  line-height: 1.2;
}

.sc-card-company {
  color: #e2495f;
  font-family: var(--font-family-heading);
  font-size: 10.5px;
  font-weight: 700;
  letter-spacing: 0.6px;
}

.sc-card-dates {
  color: var(--color-theater-text-muted);
  font-family: var(--font-family-heading);
  font-size: 10.5px;
  font-weight: 600;
  letter-spacing: 0.4px;
}

.sc-empty {
  text-align: center;
  padding: 40px 16px;
  color: rgba(255, 255, 255, 0.6);
  font-family: var(--font-family-heading);
  font-size: 13px;
}
</style>
